import { Link } from 'react-router';
import { ArrowRight, Phone, CheckCircle2, Shield, Users } from 'lucide-react';
import { APP_NAME, COURSES } from '@/lib/constants';

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <nav className="mx-auto max-w-7xl flex items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 text-primary font-bold text-xl">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 32 32" className="w-5 h-5" fill="none">
                <path d="M8 12h16M8 16h16M8 20h10" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="23" cy="13" r="5" fill="#16835D" stroke="white" strokeWidth="1.5"/>
                <path d="M21 13l1.5 1.5 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {APP_NAME}
          </Link>
          <div className="hidden sm:flex items-center gap-6 text-sm font-medium">
            <Link to="/about" className="text-text-secondary hover:text-text transition-colors">About</Link>
            <Link to="/contact" className="text-text-secondary hover:text-text transition-colors">Contact</Link>
          </div>
          <Link
            to="/apply"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark transition-colors"
          >
            Start Application
            <ArrowRight className="w-4 h-4" />
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-text leading-tight">
                Your college admission journey,
                <span className="text-primary block"> made simpler.</span>
              </h1>
              <p className="mt-6 text-lg text-text-secondary leading-relaxed max-w-xl">
                Share your academic details, verify your number and connect with an admission counsellor
                for suitable college options across India.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white hover:bg-primary-dark transition-colors shadow-sm"
                >
                  Start your application
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-white px-6 py-3 text-base font-semibold text-text hover:bg-gray-50 transition-colors"
                >
                  Talk to a counsellor
                  <Phone className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-text-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-success" />
                  Secure document submission
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  Verified counsellor support
                </span>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white rounded-xl shadow-lg border border-border p-6 space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Application Preview</p>
                    <p className="text-xs text-text-secondary">Track your admission progress</p>
                  </div>
                </div>
                {[
                  { step: 'Phone Verified', done: true },
                  { step: 'Personal Details', done: true },
                  { step: 'Academic Details', done: false, active: true },
                  { step: 'Upload Documents', done: false },
                  { step: 'Submit Application', done: false },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      item.done
                        ? 'bg-success text-white'
                        : item.active
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-text-secondary'
                    }`}>
                      {item.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : '·'}
                    </div>
                    <span className={`text-sm ${item.active ? 'font-medium text-text' : 'text-text-secondary'}`}>
                      {item.step}
                    </span>
                  </div>
                ))}
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-xs text-text-secondary">
                    <Shield className="w-3.5 h-3.5 text-success" />
                    Your documents are stored securely
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white border-y border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-text">How It Works</h2>
            <p className="mt-4 text-lg text-text-secondary">
              A simple, guided process to connect with the right admission counsellor.
            </p>
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { step: '1', title: 'Verify Phone', desc: 'Enter your mobile number and verify with a one-time password.' },
              { step: '2', title: 'Share Details', desc: 'Tell us about your academic background and achievements.' },
              { step: '3', title: 'Upload Documents', desc: 'Securely upload your marksheets and scorecards.' },
              { step: '4', title: 'Speak to Counsellor', desc: 'Our counsellor reviews your profile and contacts you.' },
              { step: '5', title: 'Explore Options', desc: 'Get personalised college recommendations and guidance.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary-light text-primary flex items-center justify-center mx-auto font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="mt-4 font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm text-text-secondary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-text">Who This Service Is For</h2>
          <p className="mt-4 text-lg text-text-secondary">
            GetIntoCollege helps students at every stage of their admission journey.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Users, title: 'Class 12 Graduates', desc: 'Looking for undergraduate programmes after completing 12th standard.' },
            { icon: Users, title: 'Graduates', desc: 'Seeking postgraduate or professional programmes after completing a degree.' },
            { icon: Users, title: 'Diploma Holders', desc: 'Exploring lateral entry or specialised diploma programmes.' },
            { icon: Users, title: 'Working Professionals', desc: 'Interested in distance learning or executive programmes.' },
            { icon: Users, title: 'Parents & Guardians', desc: 'Helping their children find suitable colleges and courses.' },
            { icon: Users, title: 'Career Changers', desc: 'Looking to switch fields through additional qualifications.' },
          ].map((item) => (
            <div key={item.title} className="bg-white border border-border rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-lg bg-primary-light flex items-center justify-center">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="mt-4 font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-text-secondary">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Courses */}
      <section className="bg-white border-y border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-text">Courses & Admission Categories</h2>
            <p className="mt-4 text-lg text-text-secondary">
              Explore the range of programmes we can help you with.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COURSES.map((course) => (
              <div key={course.id} className="flex items-center gap-3 bg-bg border border-border rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-primary-light flex items-center justify-center shrink-0">
                  <div className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium">{course.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-text">
          Ready to start your admission journey?
        </h2>
        <p className="mt-4 text-lg text-text-secondary max-w-xl mx-auto">
          Fill a simple application and a counsellor will guide you through suitable college options.
        </p>
        <Link
          to="/apply"
          className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-white hover:bg-primary-dark transition-colors shadow-sm"
        >
          Start your application
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-3">
            <div>
              <Link to="/" className="text-primary font-bold text-lg">{APP_NAME}</Link>
              <p className="mt-2 text-sm text-text-secondary">
                Independent college admission guidance and counselling platform for students across India.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-text">Quick Links</h4>
              <div className="mt-3 space-y-2">
                <Link to="/about" className="block text-sm text-text-secondary hover:text-text transition-colors">About</Link>
                <Link to="/contact" className="block text-sm text-text-secondary hover:text-text transition-colors">Contact</Link>
                <Link to="/apply" className="block text-sm text-text-secondary hover:text-text transition-colors">Start Application</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-text">Legal</h4>
              <div className="mt-3 space-y-2">
                <Link to="/privacy" className="block text-sm text-text-secondary hover:text-text transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block text-sm text-text-secondary hover:text-text transition-colors">Terms & Conditions</Link>
                <Link to="/data-deletion" className="block text-sm text-text-secondary hover:text-text transition-colors">Data Deletion</Link>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-border text-center text-xs text-text-secondary">
            <p>This is an independent admission guidance platform. We are not affiliated with any government body, university, or college.</p>
            <p className="mt-1">Submitting an application does not guarantee admission. &copy; {new Date().getFullYear()} {APP_NAME}.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
